/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import numeral from 'numeral';
import { TweenMax, TimelineMax, Power1 } from 'gsap';

import t from 'app/utils/translate';
import Theme from 'app/theme';
import AnimateOnChange from 'app/components/commons/AnimateOnChange';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import DateComponent from 'app/components/commons/DateComponent';
import Responsive from 'app/components/responsive/Responsive';
import PayoutIndicator from '../commons/PayoutIndicator';
import Icon from '../commons/Icon';
import Tooltip from '../commons/Tooltip';
import ChallengeFeeField from './ChallengeFeeField';
import ScheduledTime from './ScheduledTime';
import GoldTicketButton from './GoldTicketButton';

type Props = {
  cost?: number,
  discount?: number,
  entryCap?: number,
  entryCount?: number,
  hasEntered: boolean,
  isLive: boolean,
  isTicketChallenge?: boolean,
  isRapidfire?: boolean,
  isUnlimited: boolean,
  maxEntriesPerPerson?: number,
  payout?: string,
  payoutIcon?: string,
  payoutMessage?: string,
  points?: number,
  position?: number,
  reducedCost?: number,
  scheduledTime?: number,
  showPrize?: boolean,
  startTime?: number,
  statusLabel?: string,
  prizePool?: string,
  winnings?: number
};

const MobileLabel = styled.div(({ theme }) => ({
  color: theme.color.basis4,
  [theme.mediaQuery.desktopAndUp]: {
    display: 'none'
  }
}));

const Details = styled.div(({ theme }) => ({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 1,
  flexShrink: 1,
  flexWrap: 'nowrap',
  fontSize: '0.9rem',
  fontWeight: theme.fontWeight.normal,
  justifyContent: 'space-between',
  [theme.mediaQuery.desktopAndUp]: {
    flexWrap: 'nowrap',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    justifyContent: 'flex-end'
  },
  [theme.mediaQuery.mobileOnly]: {
    paddingRight: theme.spacing.xsmall
  }
}));

const Detail = styled.div(({ size, theme }) => ({
  display: 'flex',
  margin: 0,
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: size === 'small' ? 90 : 120,
    flexGrow: 0,
    flexShrink: 0,
    textTransform: 'uppercase'
  },
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    flexDirection: 'column',
    flexBasis: 90,
    flexGrow: 1,
    flexShrink: 0,
    textTransform: 'uppercase'
  },
  [theme.mediaQuery.tabletPortraitAndBelow]: {
    flexBasis: 80
  },
  [theme.mediaQuery.mobileOnly]: {
    flexBasis: 62
  }
}));

const DesktopDetail = styled(Detail)(({ theme }) => ({
  position: 'relative',
  lineHeight: '38px',
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    display: 'none'
  }
}));

const CenterDetail = styled(Detail)(({ theme }) => ({
  [theme.mediaQuery.mobileOnly]: {
    textAlign: 'center',
    '& > div ': {
      margin: '0 auto'
    }
  }
}));

const MobileEntryDetail = styled(CenterDetail)(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    display: 'none'
  }
}));

const PrizeDetail = styled(Detail)(({ theme }) => ({
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    '& > div:last-child ': {
      fontSize: 15
    }
  },
  [theme.mediaQuery.mobileOnly]: {
    flexGrow: 2,
    '& > div ': {
      marginLeft: 'auto'
    }
  }
}));

const TicketPrizeDetail = styled(PrizeDetail)({
  marginTop: '-15px'
});

const PrizeText = styled.div({
  flexGrow: 1,
  textAlign: 'left',
  display: 'flex',
  fontWeight: 600,
  alignItems: 'center'
});

const EntriesDetail = styled(CenterDetail)(({ theme }) => ({
  [theme.mediaQuery.mobileOnly]: {
    flexGrow: 2
  }
}));

const Quantity = styled.div(({ highlight, theme }) => ({
  color: highlight ? theme.color.success : undefined
}));

const FlexList = styled.div({
  display: 'flex',
  alignItems: 'center',
  '& >*:not(:last-child)': {
    marginRight: 4
  }
});

const timelineCreator = node => {
  const timeline = new TimelineMax();
  timeline.add(
    () =>
      TweenMax.fromTo(
        node,
        0.2,
        { css: { color: Theme.color.basis1, display: 'inline-block' } },
        { css: { color: Theme.color.warning } }
      ),
    0
  );
  timeline.add(
    () => TweenMax.fromTo(node, 0.1, { scale: 1 }, { scale: 1.2, yoyo: true, repeat: 1 }),
    0.1
  );
  timeline.add(
    () =>
      TweenMax.fromTo(
        node,
        1,
        { css: { color: Theme.color.warning } },
        {
          css: { color: Theme.color.basis1 },
          ease: Power1.easeIn,
          clearProps: 'color,scale,display'
        }
      ),
    0.7
  );
  return timeline;
};

const defaultProps = {
  cost: undefined,
  discount: undefined,
  entryCap: undefined,
  entryCount: undefined,
  isTicketChallenge: false,
  isRapidfire: false,
  maxEntriesPerPerson: undefined,
  payout: undefined,
  payoutIcon: undefined,
  payoutMessage: undefined,
  points: undefined,
  position: undefined,
  prizePool: undefined,
  reducedCost: undefined,
  scheduledTime: undefined,
  showPrize: false,
  startTime: undefined,
  statusLabel: undefined,
  winnings: undefined
};

function ChallengeRowDetails(props: Props) {
  const {
    cost,
    discount,
    entryCap,
    entryCount,
    hasEntered,
    isLive,
    isTicketChallenge,
    isRapidfire,
    isUnlimited,
    maxEntriesPerPerson,
    payout,
    payoutIcon,
    payoutMessage,
    points,
    position,
    prizePool,
    reducedCost,
    scheduledTime,
    showPrize,
    startTime,
    statusLabel,
    winnings
  } = props;

  if (hasEntered && isLive) {
    return (
      <Details>
        {position !== undefined && (
          <Detail>
            <MobileLabel>{t('Position')}: </MobileLabel>
            <div>
              #
              <AnimateOnChange animation={timelineCreator} value={position}>
                {position}{' '}
              </AnimateOnChange>
              {entryCount && ` / ${entryCount}`}
            </div>
          </Detail>
        )}
        {points !== undefined && (
          <Detail>
            <MobileLabel>{t('Points')}</MobileLabel>
            <div>
              <AnimateOnChange animation={timelineCreator} value={points}>
                {numeral(points).format('0.[00]')}
              </AnimateOnChange>
            </div>
          </Detail>
        )}
        <Detail>
          <MobileLabel>{t('Winnings')}</MobileLabel>
          {isTicketChallenge ? (
            <div>{winnings || '-'}</div>
          ) : (
            <Quantity highlight={winnings > 0}>
              <AnimateOnChange animation={timelineCreator} value={winnings}>
                {winnings !== undefined && <CurrencyIcon />}
                {numeral(winnings).format('0,0.00')}
              </AnimateOnChange>
            </Quantity>
          )}
        </Detail>
      </Details>
    );
  }

  return (
    <React.Fragment>
      <Responsive desktopAndUp>
        <Details>
          {!isLive && (
            <DesktopDetail size="small">
              <ChallengeFeeField cost={cost} discount={discount} reducedCost={reducedCost} />
            </DesktopDetail>
          )}
          <EntriesDetail>
            {entryCount !== undefined && (
              <FlexList>
                {maxEntriesPerPerson > 1 && (
                  <Tooltip title="Multi Entry">
                    <Icon type="icon-multientry" />
                  </Tooltip>
                )}
                <div>
                  <AnimateOnChange animation={timelineCreator} value={entryCount}>
                    {entryCount}
                  </AnimateOnChange>
                  <span>&nbsp;/&nbsp;</span>
                  <span>
                    {entryCap === 0
                      ? t("Unlt'd")
                      : `${entryCap}${isUnlimited && !isLive ? '+' : ''}`}
                  </span>
                </div>
              </FlexList>
            )}
          </EntriesDetail>
          {payout && (
            <DesktopDetail>
              <div>{payout}</div>
            </DesktopDetail>
          )}
          {showPrize && isTicketChallenge && (
            <Tooltip title={payoutMessage} disabled={!payoutMessage}>
              <GoldTicketButton icon={payoutIcon} />
            </Tooltip>
          )}
          {showPrize && !isTicketChallenge && (
            <PrizeDetail>
              {!isRapidfire && (
                <FlexList>
                  {payoutIcon && (
                    <Tooltip title={payoutMessage} disabled={!payoutMessage}>
                      <PayoutIndicator src={payoutIcon} />
                    </Tooltip>
                  )}
                  <PrizeText>
                    <CurrencyIcon />
                    {prizePool}
                  </PrizeText>
                </FlexList>
              )}
            </PrizeDetail>
          )}
        </Details>
      </Responsive>
      <Responsive mobile tabletPortrait tabletLandscape>
        <Details>
          <Detail>
            <MobileLabel>{t('Starts')}</MobileLabel>
            <div>
              {scheduledTime && (
                <Responsive mobile>
                  <ScheduledTime startTime={scheduledTime} format="ddd h:mmA" mobile />
                </Responsive>
              )}
              {statusLabel && <span>{statusLabel}</span>}
              {startTime && <DateComponent date={startTime} format="ddd DD MMM YYYY" />}
            </div>
          </Detail>
          {!isLive && !isRapidfire && (
            <MobileEntryDetail>
              <MobileLabel>{t('Entry')}</MobileLabel>
              <ChallengeFeeField
                cost={cost}
                discount={discount}
                reducedCost={reducedCost}
                showLabel={false}
              />
            </MobileEntryDetail>
          )}
          <EntriesDetail>
            {entryCount !== undefined && (
              <React.Fragment>
                <MobileLabel>{t('Entries')}</MobileLabel>
                <FlexList>
                  {maxEntriesPerPerson > 1 && (
                    <Tooltip title="Multi Entry">
                      <Icon type="icon-multientry" />
                    </Tooltip>
                  )}
                  <div>
                    <AnimateOnChange animation={timelineCreator} value={entryCount}>
                      {entryCount}
                    </AnimateOnChange>
                    <span>&nbsp;/&nbsp;</span>
                    <span>
                      {entryCap === 0
                        ? t("Unlt'd")
                        : `${entryCap}${isUnlimited && !isLive ? '+' : ''}`}
                    </span>
                  </div>
                </FlexList>
              </React.Fragment>
            )}
          </EntriesDetail>
          {showPrize && isTicketChallenge && (
            <Responsive tabletPortrait mobileLandscape>
              <TicketPrizeDetail>
                <Tooltip title={payoutMessage} disabled={!payoutMessage}>
                  <GoldTicketButton icon={payoutIcon} />
                </Tooltip>
              </TicketPrizeDetail>
            </Responsive>
          )}
          {showPrize && !isTicketChallenge && (
            <PrizeDetail>
              {!isRapidfire && (
                <React.Fragment>
                  <MobileLabel>{t('Prize Pool')}</MobileLabel>
                  <FlexList>
                    {payoutIcon && (
                      <Tooltip title={payoutMessage} disabled={!payoutMessage}>
                        <PayoutIndicator src={payoutIcon} />
                      </Tooltip>
                    )}
                    <PrizeText>
                      <CurrencyIcon />
                      {prizePool}
                    </PrizeText>
                  </FlexList>
                </React.Fragment>
              )}
            </PrizeDetail>
          )}
        </Details>
      </Responsive>
    </React.Fragment>
  );
}

ChallengeRowDetails.defaultProps = defaultProps;

export default ChallengeRowDetails;
